import React, { ReactElement, useMemo, memo, MutableRefObject } from 'react'
import FlatList from '@sport1/react-elements/FlatList'
import Grid from '@sport1/react-elements/Grid'
import HorizontalSpacer from '../Spacer/Horizontal'
import { Spacing, RenderListItemProps } from '../LayoutTypes'
import { BackgroundColorTypes } from '../../Theme/Fonts/FontTypes'
import { TestID } from '../../utils/PropTypesDefinitions'

type ViewToken<T> = {
    item: T
    key: string
    index: number | null
    isViewable: boolean
    section?: object
}

type ViewableItemsChanged<ItemT> = {
    viewableItems: ViewToken<ItemT>[]
    changed: ViewToken<ItemT>[]
}

type ViewabilityConfig = {
    minimumViewTime?: number
    itemVisiblePercentThreshold?: number
}

type FlatListRefType = {
    innerRef?: MutableRefObject<{
        scrollToOffset: ({ offset, animated }: { offset: number; animated?: boolean }) => void
    } | null>
}

export type HorizontalScrollViewProps<T> = {
    /** the items which are passed to the list  */
    items: T[]
    /** callback which should return a react element for each item
     *
     * @TJS-type function
     */
    renderItem: (item: RenderListItemProps<T>) => ReactElement
    /** callback which should return a unique key for each item
     *
     * @TJS-type function
     * */
    keyExtractor: (item: T, index: number) => string
    /** use the whole existing place of the container */
    expand?: boolean
    /** padding to the left and right */
    spaceX?: Spacing | Spacing[]
    /** space between items */
    spaceBetween?: Spacing | Spacing[]
    /** the color of the background
     *
     * @defaultValue tahitian-4
     */
    backgroundColor?: BackgroundColorTypes
    /** the component on top of the list */
    ListHeaderComponent?: ReactElement
    /** the component at the end of the list */
    ListFooterComponent?: ReactElement
    /** callback which passes viewable items as parameter
     *
     * @TJS-type function
     * */
    onViewableItemsChanged?: (info: ViewableItemsChanged<T>) => void
    /** config for viewableItems in NativeFlatList,
     * for example defines the percent of the item that has to be visible */
    viewabilityConfig?: ViewabilityConfig
    /** callback when scrollView triggers a scroll event
     *
     * @TJS-type function
     * */
    onScroll?: ({ scrollTop, scrollLeft }: { scrollTop?: number; scrollLeft?: number }) => void
} & TestID

const getSpacer = (
    space: Spacing | Spacing[],
    backgroundColor?: BackgroundColorTypes
): ReactElement => {
    return <HorizontalSpacer space={space} backgroundColor={backgroundColor} />
}

const getSpacerOrUndefined = (
    space?: Spacing | Spacing[],
    backgroundColor?: BackgroundColorTypes
): ReactElement | undefined => {
    if (space) return getSpacer(space, backgroundColor)
    return undefined
}

const getListEdgeComponent = (
    space?: Spacing | Spacing[],
    backgroundColor?: BackgroundColorTypes,
    component?: ReactElement
): ReactElement | undefined => {
    if (!component) {
        return getSpacerOrUndefined(space, backgroundColor)
    }
    if (!space) {
        return component
    }
    return (
        <Grid horizontal noExpand>
            {getSpacer(space, backgroundColor)}
            {component}
            {getSpacer(space, backgroundColor)}
        </Grid>
    )
}

const HorizontalScrollView = <T extends {}>({
    items,
    innerRef,
    renderItem,
    keyExtractor,
    spaceBetween,
    spaceX,
    expand,
    backgroundColor = 'tahitian-4',
    ListHeaderComponent,
    ListFooterComponent,
    onViewableItemsChanged,
    viewabilityConfig,
    onScroll,
    testID,
}: HorizontalScrollViewProps<T> & FlatListRefType): ReactElement => {
    const ItemSeparatorComponent = useMemo(() => {
        return spaceBetween
            ? (): ReactElement => getSpacer(spaceBetween, backgroundColor)
            : undefined
    }, [spaceBetween, backgroundColor])

    const MemoizedListHeaderComponent = useMemo(() => {
        return getListEdgeComponent(spaceX, backgroundColor, ListHeaderComponent)
    }, [spaceX, backgroundColor, ListHeaderComponent])

    const MemoizedListFooterComponent = useMemo(() => {
        return getListEdgeComponent(spaceX, backgroundColor, ListFooterComponent)
    }, [spaceX, backgroundColor, ListFooterComponent])

    return (
        <Grid backgroundColor={backgroundColor} noExpand={!expand} horizontal>
            <FlatList
                innerRef={innerRef}
                testID={testID}
                horizontal
                data={items}
                renderItem={renderItem}
                keyExtractor={keyExtractor}
                onViewableItemsChanged={onViewableItemsChanged}
                onScroll={onScroll}
                ItemSeparatorComponent={ItemSeparatorComponent}
                ListHeaderComponent={MemoizedListHeaderComponent}
                ListFooterComponent={MemoizedListFooterComponent}
                viewabilityConfig={viewabilityConfig}
            />
        </Grid>
    )
}

// memo is not typed correctly for generics: https://github.com/DefinitelyTyped/DefinitelyTyped/issues/37087
const typedMemo: <T>(c: T) => T = memo

export default typedMemo(HorizontalScrollView)
